import type { Descendant } from 'slate'
import { Element, Node } from 'slate'

export const isSlateObjectEmpty = (slateObject?: Descendant[] | null) => {
  if (!slateObject) return true
  const serialized = slateObject.map((n) => Node.string(n)).join('\n')
  return serialized.trim().length === 0
}

/**
 * Hashes the text to get a unique id.
 */
export function getIdFromSlateNode(node: Descendant) {
  return getFirstTextFromSlateNode(node)
    .split('')
    .reduce((acc: number, char: string) => {
      return acc + char.charCodeAt(0)
    }, 0)
    .toString()
    .slice(0, 5)
}

export function getFirstTextFromSlateNode(node?: Descendant | Descendant[]) {
  if (!node) return ''

  if (Array.isArray(node))
    return getFirstTextFromSlateNode(getFirstTextBlocks(node)[0])

  return Node.string(Element.isElement(node) ? node.children[0]! : node)
}

export function getFirstTextBlocks(
  body: Descendant[],
  options: {
    take?: number
    types?: Element['type'][]
  } = {},
): Descendant[] {
  const types = options.types ?? [
    'text',
    'heading-one',
    'heading-two',
    'heading-three',
    'paragraph',
  ]

  const hasMention = (node: Element): boolean =>
    node.type === 'mention' ||
    node.children.some((child) => Element.isElement(child) && hasMention(child))

  return body
    .filter(
      (block) =>
        Element.isElement(block) &&
        types.includes(block.type || '') &&
        (Boolean(Node.string(block).trim()) || hasMention(block)),
    )
    .slice(0, options.take ?? 2)
}

export function ensureNonEmptyChildren(node: Descendant) {
  if (!node || !Element.isElement(node)) return

  if (node.children) {
    if (node.children.length === 0) {
      node.children = [{ text: '' }]
    } else {
      node.children.forEach(ensureNonEmptyChildren)
    }
  }
}

export function slateToPlainText(nodes: Descendant[]): string {
  return nodes.map((n) => Node.string(n)).join('\n')
}

export function plainTextToSlateParagraphs(text: string): Descendant[] {
  return text
    .split('\n')
    .map((p) => ({ type: 'paragraph', children: [{ text: p }] }))
}

export function removeTrailingWhitespace(body: Descendant[]) {
  const lastNode = body[body.length - 1]
  if (
    Element.isElement(lastNode) &&
    lastNode.type === 'paragraph' &&
    Node.string(lastNode).trim() === ''
  ) {
    return body.slice(0, -1)
  }
  return body
}
